import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "./fordon";

export const Route = createFileRoute("/integritetspolicy")({
  head: () => ({
    meta: [
      { title: "Integritetspolicy – AK Husbil & Fritid" },
      {
        name: "description",
        content:
          "Så hanterar AK Husbil & Fritid personuppgifter enligt GDPR. Information om cookies, Google AdSense, analys och dina rättigheter.",
      },
      { property: "og:title", content: "Integritetspolicy – AK Husbil & Fritid" },
      {
        property: "og:description",
        content: "Så hanterar vi personuppgifter enligt GDPR — cookies, annonser och dina rättigheter.",
      },
      { property: "og:url", content: "https://www.akhusbilfritid.se/integritetspolicy" },
    ],
    links: [{ rel: "canonical", href: "https://www.akhusbilfritid.se/integritetspolicy" }],
  }),
  component: IntegritetsPage,
});

function IntegritetsPage() {
  return (
    <>
      <PageHeader
        label="Juridik · GDPR"
        title={<>Din <span className="font-italic-display text-accent">integritet</span>.</>}
        sub="Vi värnar om dina personuppgifter. Här förklarar vi vad vi samlar in, varför, och vilka rättigheter du har."
      />

      <article className="container-edge max-w-3xl py-16 md:py-20 prose-legal">
        <p className="text-sm text-muted-foreground">Senast uppdaterad: {new Date().toLocaleDateString("sv-SE")}</p>

        <Section title="1. Personuppgiftsansvarig">
          <p>
            AK Husbil &amp; Fritid, Stabsvägen 5, 952 51 Kalix, är personuppgiftsansvarig för behandlingen av
            personuppgifter på denna webbplats. Kontakt: <a href="mailto:info@akhusbilfritid.se">info@akhusbilfritid.se</a>,
            telefon 070-862 97 43.
          </p>
        </Section>

        <Section title="2. Vilka uppgifter vi samlar in">
          <ul>
            <li>Kontaktuppgifter du själv lämnar (namn, telefon, e-post) när du hör av dig.</li>
            <li>Teknisk information om besöket: IP-adress, enhet, webbläsare, sidor du besöker.</li>
            <li>Cookies och liknande tekniker för analys, annonsering och funktionalitet.</li>
          </ul>
        </Section>

        <Section title="3. Varför vi behandlar uppgifterna">
          <ul>
            <li>För att svara på din förfrågan och fullgöra avtal (rättslig grund: avtal).</li>
            <li>För att förbättra webbplatsen och förstå hur den används (berättigat intresse).</li>
            <li>För att visa relevanta annonser via Google AdSense (samtycke).</li>
            <li>För att uppfylla bokföringskrav och annan lagstiftning (rättslig förpliktelse).</li>
          </ul>
        </Section>

        <Section title="4. Cookies och tredjepartstjänster">
          <p>
            Vi använder cookies för att webbplatsen ska fungera och för att mäta trafik. Tredjepartsleverantörer
            — bland annat Google — kan placera cookies för att visa annonser baserat på dina tidigare besök
            här eller andra webbplatser.
          </p>
          <ul>
            <li>
              <strong>Google AdSense:</strong> Google använder cookien <em>DoubleClick DART</em> för att visa
              annonser. Du kan välja bort personaliserade annonser via{" "}
              <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
                Googles annonsinställningar
              </a>{" "}
              eller{" "}
              <a href="https://www.aboutads.info/" target="_blank" rel="noopener noreferrer">
                aboutads.info
              </a>.
            </li>
            <li>
              <strong>Analys:</strong> Aggregerad besöksstatistik för att förbättra innehållet.
            </li>
          </ul>
          <p>Se vår separata <a href="/cookies">cookiepolicy</a> för fullständig lista.</p>
        </Section>

        <Section title="5. Hur länge vi sparar uppgifter">
          <p>
            Vi sparar uppgifter endast så länge det är nödvändigt för ändamålet eller enligt lag (t.ex.
            bokföringslagen, 7 år).
          </p>
        </Section>

        <Section title="6. Dina rättigheter">
          <p>
            Du har rätt att begära tillgång till, rättelse eller radering av dina uppgifter, samt att invända
            mot eller begränsa behandlingen. Kontakta oss på{" "}
            <a href="mailto:info@akhusbilfritid.se">info@akhusbilfritid.se</a>. Du kan även lämna klagomål till
            Integritetsskyddsmyndigheten (IMY).
          </p>
        </Section>

        <Section title="7. Ändringar">
          <p>Vi kan komma att uppdatera denna policy. Den senaste versionen finns alltid på denna sida.</p>
        </Section>
      </article>
    </>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mt-10">
      <h2 className="font-display text-2xl md:text-3xl">{title}</h2>
      <div className="mt-3 space-y-3 text-foreground/85 [&_a]:text-accent [&_a]:underline-offset-4 [&_a:hover]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5">
        {children}
      </div>
    </section>
  );
}
